/**
 * React Query hooks for the firm-facing Outlook integration.
 *
 * Uses `directApiClient` (client-side) per the services convention. The
 * connect / reauthenticate / admin-consent mutations return a Microsoft URL the
 * caller redirects to; the connect-shared-mailbox / disconnect mutations return
 * the refreshed status and invalidate the cached query.
 */

'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directApiClient } from '@/lib/api/direct-client';
import { useOptionalFeatureFlags } from '@/lib/providers/feature-flags-provider';
import { useCurrentUserEmail } from '@/lib/services/profile/hooks';
import { outlookSendsFromCurrentUser } from './send-identity';
import {
  OUTLOOK_API_ROUTES,
  OUTLOOK_FEATURE_FLAG,
  OUTLOOK_STATUS_QUERY_KEY
} from './constants';
import type {
  OutlookIdentityMode,
  OutlookAdminConsentHandoffResponse,
  OutlookAuthorizeUrlResponse,
  OutlookConnectStartRequest,
  OutlookConsentUrlResponse,
  OutlookFirmSendIdentityRequest,
  OutlookReauthenticateRequest,
  OutlookSharedMailboxConnectRequest,
  OutlookStatus
} from './types';

/** Firm activation + mailbox connections. Refetches on mount (after OAuth round-trips). */
export function useOutlookStatus(enabled = true) {
  return useQuery({
    queryKey: OUTLOOK_STATUS_QUERY_KEY,
    queryFn: () =>
      directApiClient.get<OutlookStatus>(OUTLOOK_API_ROUTES.status),
    enabled,
    staleTime: 30 * 1000
  });
}

/**
 * True when Outlook is flag-enabled AND active for the firm — either identity
 * mode (lawyer or shared). A lean, firm-wide selector: no matter context and
 * no mailbox-address resolution. Safe to call from any client component — the
 * feature-flag read is provider-optional and the status query is deduped by
 * React Query, so multiple call sites share one request.
 *
 * This answers "is Outlook the transport for this firm", nothing more. It is
 * NOT the right gate for the "Send me a copy" (BCC-self) control — that needs
 * to know whether the *acting user* is the mailbox that will send. Use
 * `useOutlookSendsFromCurrentUser` for that (LF-5664).
 */
export function useOutlookActive(): boolean {
  const { features } = useOptionalFeatureFlags();
  const isFlagged = features.includes(OUTLOOK_FEATURE_FLAG);
  const { data } = useOutlookStatus(isFlagged);
  return isFlagged && data?.activationState === 'active';
}

/** Shape of the matter's acting solicitor as every matter read already exposes it. */
export interface ActingSolicitorLike {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

export interface OutlookMatterSender {
  /** Outlook is flag-enabled AND active for the firm. */
  active: boolean;
  identityMode: OutlookIdentityMode | null;
  /**
   * True when the acting user IS the mailbox that will send, so the message
   * lands in their own Sent Items. This — not `active` — is the gate for
   * suppressing "Send me a copy".
   */
  sendsFromCurrentUser: boolean;
  /**
   * Display name of the sending identity, in lawyer mode only. Null in shared
   * mode (a firm mailbox is not a person) and when no acting solicitor is set.
   */
  senderName: string | null;
  /** Email of the sending identity, in lawyer mode only. */
  senderEmail: string | null;
}

/**
 * Resolves this matter's Outlook sending identity for send surfaces that have
 * NO Communications thread header — Discovery, the bulk correspondence
 * workflows, Redemption.
 *
 * Those surfaces cannot see the backend-resolved
 * `ThreadHeader.outlookSendIdentity`, but in lawyer mode the sending identity
 * *is* `matter.actingSolicitor`, so "who sends, and is it me?" reduces to the
 * matter data they already load — no extra fetch, no thread-items plumbing.
 * Surfaces that DO have the thread header must use
 * `outlookSendIdentity.mailboxAddress` instead: that is the authoritative
 * backend resolution (LF-5664).
 */
export function useOutlookMatterSender(
  actingSolicitor: ActingSolicitorLike | null | undefined
): OutlookMatterSender {
  const { features } = useOptionalFeatureFlags();
  const isFlagged = features.includes(OUTLOOK_FEATURE_FLAG);
  const { data } = useOutlookStatus(isFlagged);
  const { email: currentUserEmail } = useCurrentUserEmail();

  const active = isFlagged && data?.activationState === 'active';
  const identityMode = active ? (data?.identityMode ?? null) : null;
  const isLawyerMode = identityMode === 'lawyer';
  const fullName = [actingSolicitor?.firstName, actingSolicitor?.lastName]
    .filter((part): part is string => !!part && part.length > 0)
    .join(' ');

  return {
    active,
    identityMode,
    sendsFromCurrentUser: outlookSendsFromCurrentUser({
      outlookActive: active,
      identityMode,
      currentUserEmail,
      actingSolicitorEmail: actingSolicitor?.email
    }),
    senderName: isLawyerMode && fullName ? fullName : null,
    senderEmail: isLawyerMode ? (actingSolicitor?.email ?? null) : null
  };
}

/** Begin tenant admin consent — returns the Microsoft grant URL to redirect to. */
export function useStartOutlookAdminConsent() {
  return useMutation({
    mutationFn: () =>
      directApiClient.post<OutlookConsentUrlResponse>(
        OUTLOOK_API_ROUTES.adminConsentStart
      )
  });
}

/**
 * Mint a shareable Microsoft-365 authorisation link for the firm's external
 * Microsoft administrator (admin only, VALE-407).
 *
 * Returns the URL **once** — nothing re-serves it, so the caller shows it to the
 * administrator to copy. The refreshed status is invalidated rather than written
 * from the response, because this response carries the link, not the status.
 */
export function useIssueOutlookAdminConsentHandoff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      directApiClient.post<OutlookAdminConsentHandoffResponse>(
        OUTLOOK_API_ROUTES.adminConsentHandoff
      ),
    onSuccess: () => {
      // The card's outstanding-link readout comes from `GET status`, which this
      // has just changed — and issuing also *creates* the activation row for a
      // firm that had none, so `activationState` moves too.
      void queryClient.invalidateQueries({ queryKey: OUTLOOK_STATUS_QUERY_KEY });
    }
  });
}

/** Kill the outstanding hand-off link (admin only) — returns the refreshed status. */
export function useRevokeOutlookAdminConsentHandoff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      directApiClient.post<OutlookStatus>(
        OUTLOOK_API_ROUTES.adminConsentHandoffRevoke
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(OUTLOOK_STATUS_QUERY_KEY, data);
    }
  });
}

/**
 * Choose how the firm sends matter email (admin only) — the post-consent step.
 *
 * Returns the refreshed status, so the card re-renders straight into the next
 * step (connect a mailbox) with no refetch round trip.
 */
export function useSetOutlookSendIdentity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OutlookFirmSendIdentityRequest) =>
      directApiClient.post<OutlookStatus, OutlookFirmSendIdentityRequest>(
        OUTLOOK_API_ROUTES.sendIdentity,
        body
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(OUTLOOK_STATUS_QUERY_KEY, data);
    }
  });
}

/** Begin a per-user mailbox connect — returns the Microsoft authorize URL. */
export function useConnectOutlook() {
  return useMutation({
    mutationFn: (body: OutlookConnectStartRequest) =>
      directApiClient.post<
        OutlookAuthorizeUrlResponse,
        OutlookConnectStartRequest
      >(OUTLOOK_API_ROUTES.connect, body)
  });
}

/**
 * Re-run sign-in for an expiring/broken connection — returns the authorize URL.
 *
 * `returnTo` names the surface the user started from so the post-OAuth callback
 * sends them back there rather than to the admin-only Integrations tab.
 */
export function useReauthenticateOutlookConnection() {
  return useMutation({
    mutationFn: ({
      connectionId,
      returnTo
    }: { connectionId: number } & OutlookReauthenticateRequest) =>
      directApiClient.post<
        OutlookAuthorizeUrlResponse,
        OutlookReauthenticateRequest
      >(OUTLOOK_API_ROUTES.reauthenticate(connectionId), { returnTo })
  });
}

/** Register a shared mailbox against a connected principal; kicks off scope verification. */
export function useConnectOutlookSharedMailbox() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OutlookSharedMailboxConnectRequest) =>
      directApiClient.post<OutlookStatus, OutlookSharedMailboxConnectRequest>(
        OUTLOOK_API_ROUTES.sharedMailboxConnect,
        body
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(OUTLOOK_STATUS_QUERY_KEY, data);
    }
  });
}

/** Forward-only disconnect — returns the refreshed status (reply capture stops, D-12). */
export function useDisconnectOutlookConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: number) =>
      directApiClient.post<OutlookStatus>(
        OUTLOOK_API_ROUTES.disconnect(connectionId)
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(OUTLOOK_STATUS_QUERY_KEY, data);
    }
  });
}

/**
 * Turn the firm's whole Microsoft 365 connection off (admin only) — disconnects
 * every mailbox and moves the firm to `disabled`. Edit in Word stops with it:
 * both capabilities ride the one consent, so this is a firm-wide action, not a
 * mail-only one. Returns the refreshed status.
 */
export function useDisableM365Connection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      directApiClient.post<OutlookStatus>(OUTLOOK_API_ROUTES.disable),
    onSuccess: (data) => {
      queryClient.setQueryData(OUTLOOK_STATUS_QUERY_KEY, data);
    }
  });
}
